import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog.jsx';
import { Button } from '@/components/ui/button.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { RefreshCw } from 'lucide-react';
import { regenerateBindingApp, REGEN_REFUSALS } from '@/services/deals.service.js';
import { ApiError } from '@/lib/api.js';

// THE REASON SHOWN IS THE REASON GIVEN (review G5-R9). Each refusal the server
// names has its own headline and its own next step, and the server's message
// (the pilots and what changed, or what is missing) prints beneath; a fixed
// "a pilot form has changed" over SIGNED_WARRANTIES_MISSING sent David looking
// for a changed form that does not exist and told him a re-attestation would
// fix a missing warranty block, which it cannot. A code this map does not know
// gets the server's message alone, never a reason it did not give.
const REFUSAL_COPY = {
  FORM_CHANGED_SINCE_SIGNING: {
    headline: 'Not regenerated: the application on file was signed over a pilot form that has changed since.',
    next: 'The signed PDF on file stands. Regenerate after the pilot re-attests and the application is re-signed.',
    toast: 'Not regenerated: a signed pilot form has changed. See the dialog.',
  },
  SIGNED_WARRANTIES_MISSING: {
    headline: 'Not regenerated: the application on file carries no signed pilot warranty answers.',
    next: 'The signed PDF on file stands. The answers the client signed were not recorded with the signature, so the application cannot be reproduced; file the signed PDF as it is, or have the application re-signed.',
    toast: 'Not regenerated: the signed warranty answers are missing. See the dialog.',
  },
  NOT_AN_AVIATION_TERM: {
    headline: 'Not regenerated: this term is not aviation, and its application on file is not a binding application.',
    next: 'The application on file stands. A hangar term\'s packet is rebuilt from its own Regenerate hangar packet.',
    toast: 'Not regenerated: not an aviation term. See the dialog.',
  },
};
const copyFor = (code) => REFUSAL_COPY[code] || { headline: 'Not regenerated.', next: 'The signed PDF on file stands.', toast: 'Not regenerated. See the dialog.' };

// "Regenerate binding app" confirm (BINDING-DOCS-FIXES item C). Rebuilds the
// binding application PDF from current CRM + Supabase state and OVERWRITES the
// client's binding app in WorkDrive, so the broker must confirm first. Emails
// no one. On a deal WITH a session the client signature captured at bind time
// is reproduced (and the carrier TRIA disclosure page re-appended); a
// session-less deal regenerates with the paper-original placeholder, which the
// copy states plainly. Never fires on load.
export default function RegenerateBindingAppDialog({ open, onOpenChange, dealId, accountName, hasSession, onRegenerated }) {
  const [submitting, setSubmitting] = useState(false);
  // THE REFUSAL RENDERS HERE, where the button was pressed (attestation S4-8):
  // a bind that recorded the forms it printed refuses to regenerate over a
  // pilot form attested again, cleared or gone since the signature, and the
  // server names the pilots. A toast alone would scroll away; the dialog keeps
  // the names in front of the broker until it is closed.
  const [refusal, setRefusal] = useState(null);

  async function handleRegenerate() {
    setSubmitting(true);
    setRefusal(null);
    try {
      const res = await regenerateBindingApp(dealId, { usePersistedSignature: !!hasSession });
      if (!res?.success) {
        toast.error(`Couldn't regenerate: ${res?.error || 'unknown error'}`);
        return;
      }
      toast.success(res.message || 'Binding application regenerated');
      // A name the application printed blank, or could not check (ruling 4, F3):
      // one toast each, on the surface that fired the success toast.
      for (const w of Array.isArray(res.warnings) ? res.warnings : []) toast.info(w);
      onOpenChange?.(false);
      if (typeof onRegenerated === 'function') onRegenerated(res.link || null);
    } catch (err) {
      const refused = err instanceof ApiError && REGEN_REFUSALS.some((r) => r.status === err.status && r.code === err.body?.code);
      if (refused) {
        setRefusal({ code: err.body.code, message: err.body.error || '', pilots: Array.isArray(err.body.pilots) ? err.body.pilots : [] });
        toast.error(copyFor(err.body.code).toast);
        return;
      }
      const msg = err instanceof ApiError ? err.body?.error || err.code : err.message || 'request failed';
      toast.error(`Couldn't regenerate: ${msg}`);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw size={18} className="text-accent" />
            Regenerate binding app
          </DialogTitle>
          <DialogDescription>
            This replaces the binding application in <strong className="text-text">{accountName || 'this client'}</strong>'s
            WorkDrive folder with a freshly rendered copy from the current policy data. The old file is superseded and the
            signed-application link updates. No email or text is sent.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-1 rounded border border-border bg-surface-strong px-3 py-2.5 text-[13px] text-text-muted">
          {hasSession
            ? 'The client signature captured at binding, and the carrier terrorism (TRIA) disclosure page, are reproduced on the regenerated application.'
            : 'This deal has no signing session on file, so the application regenerates with a "paper original on file" placeholder in the signature area.'}
        </div>
        {refusal && (
          <div role="alert" data-refusal={refusal.code} className="mt-2 rounded border border-danger/40 bg-danger/10 px-3 py-2.5 text-[13px] text-text">
            <div className="font-semibold">{copyFor(refusal.code).headline}</div>
            {refusal.message && <div className="mt-1.5 whitespace-pre-line" data-refusal-message>{refusal.message}</div>}
            {refusal.pilots.length > 0 && (
              <ul className="mt-1.5 list-disc pl-5">
                {refusal.pilots.map((p) => (
                  <li key={`${p.phf_id || p.contact_id || p.name}`}>
                    {p.name}: {p.reason === 'missing' ? 'the form is gone' : p.reason === 'cleared' ? 'the attestation was taken back' : 'the form was attested again'}
                  </li>
                ))}
              </ul>
            )}
            <div className="mt-1.5 text-text-muted">{copyFor(refusal.code).next}</div>
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange?.(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button type="button" onClick={handleRegenerate} disabled={submitting}>
            {submitting ? 'Regenerating…' : 'Replace binding app'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
